import { useEffect, useState } from 'react';
import { Box, Button, Dialog, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { Lottie } from 'lottie-react';
import { Gift, UsersRound, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { paths } from 'src/routes/paths';
import giftAnimation from 'src/assets/animation/Gift.json';
import { markFirstVisit } from 'src/auth/context/jwt';

import { getCampaignInfo } from './referral-campaign';

const ACCOUNT_TAB_KEY = 'account_view_tab';
const REFERRAL_TAB_EVENT = 'fitnext:open-referral-tab';

const PURPLE = '#7C2FE8';
const PURPLE_DARK = '#6A22CE';
const LAVENDER = '#F0E9FD';

const MODAL_OPEN_DELAY_MS = 3000; // 3 sec baad modal khulega
const BUBBLE_SHOW_DELAY_MS = 1500; // 1.5 sec baad bubble dikhega

export function ReferralRewardsPromo({ user }) {
  const [open, setOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(true);

  const navigate = useNavigate();
  const { discountLabel, rewardLabel } = getCampaignInfo(user?.campaign);
  const limit = Number(user?.campaign?.referral_limit) || 0;
  const isDark = useTheme().palette.mode === 'dark';
  const surface = isDark ? '#1C252E' : '#fff';
  const titleColor = isDark ? '#F4F6F8' : '#111827';
  const textColor = isDark ? '#919EAB' : '#6B7280';
  const iconColor = isDark ? '#C4CDD5' : '#4B5563';
  const accent = isDark ? '#A97BF5' : PURPLE;
  const pillBg = isDark ? 'rgba(124, 47, 232, 0.16)' : LAVENDER;

  // 👇 Har render par localStorage check karo (lekin remove mat karo)
  const isSignupFlow = localStorage.getItem('flow_just_signed_up') === 'true';
  const signedUpWithReferral = localStorage.getItem('flow_signed_up_with_referral') === 'true';
  const shouldHideEntirely = isSignupFlow && !signedUpWithReferral;

  // useEffect(() => {
  //   if (isSignupFlow) {
  //     setOpen(false);
  //     return;
  //   }

  //   if (user?.id && user?.role?.id !== 1 && user?.is_first_visit === false) {
  //     setOpen(true);
  //   }
  // }, [user?.id, user?.role?.id, user?.is_first_visit, isSignupFlow]);

  useEffect(() => {
    if (isSignupFlow || !user?.referred_by) {
      setOpen(false);
      return undefined;
    }

    const shouldOpen = user?.id && user?.role?.id !== 1 && user?.is_first_visit === false;
    if (!shouldOpen) return undefined;

    const timer = setTimeout(() => {
      setOpen(true);
    }, MODAL_OPEN_DELAY_MS);

    return () => clearTimeout(timer);
  }, [user?.id, user?.role?.id, user?.is_first_visit, user?.referred_by, isSignupFlow]);

  // 👇 Bubble ko bhi delay se dikhao
  useEffect(() => {
    if (isSignupFlow || shouldHideEntirely || !user?.referred_by) return undefined;

    const timer = setTimeout(() => {
      setShowBubble(true);
    }, BUBBLE_SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isSignupFlow, shouldHideEntirely, user?.referred_by]);

  const handleCloseModal = async () => {
    setOpen(false);
    try {
      await markFirstVisit();
    } catch (error) {
      console.error(error);
    }
  };

  const openReferralTab = async () => {
    setOpen(false);
    localStorage.setItem(ACCOUNT_TAB_KEY, 'referral');
    window.dispatchEvent(new Event(REFERRAL_TAB_EVENT));
    navigate(paths.dashboard.general.account);

    try {
      await markFirstVisit();
    } catch (error) {
      console.error(error);
    }
  };

  // Agar admin hai, ya signup without referral hai, to kuch bhi render mat karo
  if (!user?.id || user?.role?.id === 1 || !user?.referred_by || shouldHideEntirely) {
    return null;
  }

  return (
    <>
      {showBubble && !shouldHideEntirely && (
        <Box
          sx={{
            position: 'fixed',
            right: { xs: 20, sm: 28 },
            bottom: { xs: 90, sm: 98 },
            zIndex: (theme) => theme.zIndex.fab,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            width: 160,
            px: 1.75,
            py: 1.5,
            borderRadius: 1.5,
            bgcolor: surface,
            boxShadow: '0 4px 16px rgba(16, 24, 40, 0.14)',
            '&::after': {
              content: '""',
              position: 'absolute',
              right: 26,
              bottom: -6,
              width: 12,
              height: 12,
              bgcolor: surface,
              transform: 'rotate(45deg)',
            },
          }}
        >
          <Typography sx={{ flex: 1, fontSize: 12.5, lineHeight: 1.45, color: titleColor }}>
            Earn rewards by referring friends!
          </Typography>
          <IconButton
            aria-label="Dismiss"
            size="small"
            onClick={() => setShowBubble(false)}
            sx={{ p: 0, mt: -0.25, color: textColor }}
          >
            <X size={14} />
          </IconButton>
        </Box>
      )}

      <IconButton
        aria-label="Earn rewards by referring friends"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          right: { xs: 20, sm: 32 },
          bottom: { xs: 20, sm: 32 },
          zIndex: (theme) => theme.zIndex.fab,
          width: 56,
          height: 56,
          color: '#fff',
          bgcolor: PURPLE,
          boxShadow: '0 4px 14px rgba(124, 58, 237, 0.4)',
          '&:hover': { bgcolor: PURPLE_DARK },
        }}
      >
        <Gift size={26} />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleCloseModal}
        onBackdropClick={handleCloseModal}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 450,
            maxWidth: 'calc(100% - 32px)',
            borderRadius: 1.5,
            px: 3.5,
            pt: 4,
            pb: 3,
            textAlign: 'center',
            bgcolor: surface,
            backgroundImage: 'none',
          },
        }}
      >
        <IconButton
          aria-label="Close"
          onClick={handleCloseModal}
          sx={{ position: 'absolute', top: 12, right: 12, color: iconColor }}
        >
          <X size={18} />
        </IconButton>

        <Box sx={{ width: 200, height: 200, mx: 'auto', mb: 1 }}>
          <Lottie src={giftAnimation} autoplay />
        </Box>

        <Typography
          sx={{ fontSize: 22, fontWeight: 800, color: titleColor, lineHeight: 1.3, marginTop: -5 }}
        >
          Earn Rewards With FitNex!
        </Typography>
        <Typography sx={{ mt: 1.25, fontSize: 13, lineHeight: 1.6, color: textColor }}>
          {rewardLabel ? `Get ${rewardLabel} in store credit` : 'Get store credit'} for every active
          friend you refer.
          {discountLabel ? ` Your friends get ${discountLabel} off Plus!` : ''}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1.25}
          sx={{ mt: 2.5, py: 1.5, px: 1.5, borderRadius: 1, color: accent, bgcolor: pillBg }}
        >
          <UsersRound size={20} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: accent }}>
            {limit ? `First ${limit} active referrals only.` : 'Limited active referrals only.'}
          </Typography>
        </Stack>

        <Button
          fullWidth
          onClick={openReferralTab}
          sx={{
            mt: 2.5,
            height: 44,
            borderRadius: 1,
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            textTransform: 'none',
            bgcolor: PURPLE,
            '&:hover': { bgcolor: PURPLE_DARK },
          }}
        >
          Start Earning Rewards
        </Button>
        <Button
          onClick={handleCloseModal}
          sx={{
            mt: 1.5,
            p: 0,
            minWidth: 0,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            color: accent,
            '&:hover': { bgcolor: 'transparent', color: PURPLE_DARK },
          }}
        >
          Maybe Later
        </Button>
      </Dialog>
    </>
  );
}
